import { ClassyURLParams } from '../params/params.types'
import { ConfigResponse } from '../config/config.types'
import { getComponentSectionByType } from '../config/config.utils'
import { getIframeBaseUri, getIframeRouteReason, getSdkScriptOrigin } from '../util/script-params'

// Some host pages can force a 320px viewport; 400px preserves checkout grid side gutters.
const NARROW_VIEWPORT_THRESHOLD = 376
const NARROW_CHECKOUT_IFRAME_LAYOUT_WIDTH = 400

export interface ModalViewProps {
  campaign: string
  params: ClassyURLParams
  subscribeToCloseEvent: () => void
  onIframeLoad?: (campaign: string) => void
  config?: ConfigResponse
  pgdid?: string
}

export default class ModalView extends HTMLElement {
  private props: ModalViewProps

  private loader: HTMLDivElement
  private iframe: HTMLIFrameElement
  private closeBtn: HTMLButtonElement
  private readonly syncNarrowViewportScale = () => this._syncNarrowViewportScale()

  constructor(props: ModalViewProps) {
    super()
    this.props = props

    const shadowRoot = this.attachShadow({ mode: 'open' })

    this.loader = this._makeLoader()
    this.iframe = this._makeIframe()
    this.closeBtn = this._makeCloseBtn()

    shadowRoot.append(this._makeCSS())
    shadowRoot.append(this._makeHTML())
    this._syncNarrowViewportScale()
    this._bindViewportEvents()

    this.iframe.addEventListener('load', () => {
      this.loader.remove()
      this.props.onIframeLoad?.(this.props.campaign)
    })
  }

  public getIframe = (): HTMLIFrameElement => this.iframe

  public getCampaign = (): string => this.props.campaign

  public getParams = (): ClassyURLParams => this.props.params

  disconnectedCallback(): void {
    this._unbindEvents()
  }

  private _bindViewportEvents = () => {
    const win = this.ownerDocument.defaultView

    win?.addEventListener('resize', this.syncNarrowViewportScale)
    win?.visualViewport?.addEventListener('resize', this.syncNarrowViewportScale)
  }

  private _isContentPanelEnabled = (): boolean => {
    if (!this.props.config) {
      return false
    }

    const modalComponent = getComponentSectionByType('checkout', 'content-pane', this.props.config)

    return modalComponent?.props.enabled === true
  }

  private _unbindEvents = () => {
    this.closeBtn.removeEventListener('click', this.props.subscribeToCloseEvent)

    const win = this.ownerDocument.defaultView

    win?.removeEventListener('resize', this.syncNarrowViewportScale)
    win?.visualViewport?.removeEventListener('resize', this.syncNarrowViewportScale)
  }

  private _syncNarrowViewportScale = () => {
    const win = this.ownerDocument.defaultView
    const viewportWidth =
      this.ownerDocument.documentElement.clientWidth || win?.innerWidth || NARROW_VIEWPORT_THRESHOLD

    if (viewportWidth >= NARROW_VIEWPORT_THRESHOLD) {
      this.removeAttribute('data-eg-narrow-viewport')
      this.style.removeProperty('--eg-modal-iframe-scale')
      this.style.removeProperty('--eg-modal-iframe-layout-width')
      this.style.removeProperty('--eg-modal-iframe-layout-height')
      return
    }

    const scale = viewportWidth / NARROW_CHECKOUT_IFRAME_LAYOUT_WIDTH

    this.setAttribute('data-eg-narrow-viewport', '')
    this.style.setProperty('--eg-modal-iframe-scale', String(scale))
    this.style.setProperty(
      '--eg-modal-iframe-layout-width',
      `${NARROW_CHECKOUT_IFRAME_LAYOUT_WIDTH}px`
    )
    this.style.setProperty('--eg-modal-iframe-layout-height', `${100 / scale}%`)
  }

  private _makeCSS = () => {
    const css = document.createElement('style')

    css.textContent = `
      .eg-modal {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        height: calc(100dvh - 45px);
        background: rgba(26, 25, 25, 0.8);
        z-index: 2147483645;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: auto;
        padding-top: 45px;
      }

      .eg-modal--window {
        position: relative;
        height: 100%;
        min-width: 0;
        max-width: 100vw;
        border-radius: 20px;
        max-height: 680px;
        pointer-events: auto;
        display: flex;
        flex-direction: column;
      }

      .eg-modal--close {
        position: absolute;
        top: -36px;
        right: 0px;
        cursor: pointer;
        z-index: 2;
        border: none;
        background: transparent;
        height: 24px;
        color: var(--neutral-100, #FFF);;
        text-align: center;
        font-family: sans-serif;
        font-size: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: color 0.2s ease;
      }

      .eg-modal--close:hover {
        opacity: 0.8;
      }
      .eg-modal--close-x {
        font-size: 20px;
        margin-left: 3px;
        margin-bottom: 2px;
      }

      .eg-modal--iframe {
        flex: 1;
        width: var(--eg-modal-iframe-layout-width, 100%);
        border: none;
        border-radius: 20px;
        z-index: 1;
      }

      :host([data-eg-narrow-viewport]) .eg-modal--iframe {
        align-self: center;
        flex: 0 0 var(--eg-modal-iframe-layout-height, 100%);
        height: var(--eg-modal-iframe-layout-height, 100%);
        max-width: none;
        transform: scale(var(--eg-modal-iframe-scale, 1));
        transform-origin: top center;
      }

      .eg-modal--loader {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 2;
        display: flex;
        align-items: center;
        justify-content: center;
        animation: zoom-in 0.25s ease;
        background: white;
        border-radius: 20px;
      }

      .eg-modal--loader--inner {
        padding: 48px;
        background: white;
        border-radius: 20px;
        text-align: center;
        color: #1B1918;
      }

      .eg-modal--loader--title {
        font-size: 1.125em;
        
      }

      .eg-modal--loader--body {
        font-size: .85em;
      }

      @keyframes zoom-in {
        0% {
          transform: scale(0.5, 0.5);
        }
        100% {
          transform: scale(1, 1);
        }
      }

      @media (max-width: 783px) {
        .eg-modal--window {
          width: min(440px, 100vw) !important;
        }
      }
    `

    return css
  }

  private _makeHTML = () => {
    const modal = document.createElement('div')
    modal.setAttribute('class', 'eg-modal')

    const window = document.createElement('div')
    window.setAttribute('class', 'eg-modal--window')

    // Apply dynamic width based on content panel setting
    const width = this._isContentPanelEnabled() ? '992px' : '440px'
    window.style.width = width

    window.appendChild(this.closeBtn)
    window.appendChild(this.iframe)
    window.appendChild(this.loader)

    modal.appendChild(window)

    return modal
  }

  private _makeIframe = () => {
    const iframe = document.createElement('iframe')
    const iframeTitle = this.props.config?.organizationName
      ? `Donate to ${this.props.config.organizationName}`
      : 'Donate'

    iframe.setAttribute('frameborder', '0')
    iframe.setAttribute('allow', 'payment; clipboard-write; clipboard-read;')
    iframe.setAttribute('allowfullscreen', 'true')
    iframe.setAttribute('allowpaymentrequest', 'true')
    iframe.setAttribute('class', 'eg-modal--iframe')
    iframe.setAttribute('name', `eg-modal-iframe--${this.props.campaign}`)
    iframe.setAttribute('src', this._makeIframeSrc())
    iframe.setAttribute('title', iframeTitle)

    return iframe
  }

  private _makeLoader = () => {
    const loader = document.createElement('div')
    loader.setAttribute('class', 'eg-modal--loader')

    loader.innerHTML = `
      <div class="eg-modal--loader--inner">
      <svg xmlns="http://www.w3.org/2000/svg" width="3em" height="3em" viewBox="0 0 24 24">
        <path fill="#425CCD" d="M10.72,19.9a8,8,0,0,1-6.5-9.79A7.77,7.77,0,0,1,10.4,4.16a8,8,0,0,1,9.49,6.52A1.54,1.54,0,0,0,21.38,12h.13a1.37,1.37,0,0,0,1.38-1.54,11,11,0,1,0-12.7,12.39A1.54,1.54,0,0,0,12,21.34h0A1.47,1.47,0,0,0,10.72,19.9Z">
          <animateTransform attributeName="transform" dur="0.75s" repeatCount="indefinite" type="rotate" values="0 12 12;360 12 12" />
        </path>
      </svg>
        <div class="eg-modal--loader--title">One moment, please</div>
      </div>
    `

    return loader
  }

  private _makeCloseBtn = () => {
    const btn = document.createElement('button')
    btn.innerHTML =
      '<span class="eg-modal--close-text">Close</span> <span class="eg-modal--close-x">&times;</span>'
    btn.setAttribute('label', 'Close')
    btn.setAttribute('class', 'eg-modal--close')
    btn.addEventListener('click', this.props.subscribeToCloseEvent)

    return btn
  }

  private _makeIframeSrc = () => {
    const appUri = getIframeBaseUri()

    // @ts-ignore global variable defined by webpack
    const isProd = process.env.ENV === 'production'

    const urlParamsStr = new URLSearchParams({
      cid: this.props.campaign,
      ...this.props.params,
      ...(this.props.pgdid && { pgdid: this.props.pgdid }),
      // Routing telemetry (PRO-6540), appended last so host-page pass-through
      // params can't override it.
      sdk_build: process.env.SDK_BUILD_ID || 'unknown',
      sdk_origin: getSdkScriptOrigin(),
      sdk_route: getIframeRouteReason(),
    }).toString()

    // This url hash prevents the consent popup from showing. Needed by TE
    const hideConsentPromptURLHash = isProd ? '' : '#:~:tcm-prompt=Hidden&log=*'

    return `${appUri}/?${urlParamsStr}${hideConsentPromptURLHash}`
  }
}

// Check if 'eg-modal' is already defined before defining it again
if (!customElements.get('eg-modal')) {
  customElements.define('eg-modal', ModalView)
}
